import { Routes } from '@angular/router';
import { AuthLayout } from './core/layouts/auth-layout/auth-layout';
import { AdminLayout } from './core/layouts/admin-layout/admin-layout';
import { Login } from './features/authentication/login/login';
import { AdminLogin } from './features/authentication/admin-login/admin-login';
import { Register } from './features/authentication/register/register';
import { ForgotPassword } from './features/authentication/forgot-password/forgot-password';
import { ResetPassword } from './features/authentication/reset-password/reset-password';
import { ChangePassword } from './features/authentication/change-password/change-password';
import { Dashboard } from './features/dashboard/dashboard';
import { Users } from './features/users/users';
import { Roles } from './features/roles/roles';
import { Permissions } from './features/permissions/permissions';
import { Products } from './features/products/products';
import { Inventory } from './features/inventory/inventory';
import { Billing } from './features/billing/billing';
import { Reports } from './features/reports/reports';
import { Stores } from './features/stores/stores';
import { Catalog } from './features/catalog/catalog';
import { Sales } from './features/sales/sales';
import { BusinessOnboarding } from './features/business-onboarding/business-onboarding';
import { authGuard } from './core/guards/auth.guard';
import { guestGuard } from './core/guards/guest.guard';
import { businessOnboardingGuard, businessOwnerOnlyGuard, businessPlanSelectionGuard } from './core/guards/business-onboarding.guard';
import { Settings } from './features/settings/settings';
import { SupportTickets } from './features/support-tickets/support-tickets';
import { Faqs } from './features/faqs/faqs';
import { ListingApprovals } from './features/listing-approvals/listing-approvals';
import { Purchases } from './features/purchases/purchases';
import { PurchaseList } from './features/purchase-list/purchase-list';
import { Finance } from './features/finance/finance';
import { permissionGuard } from './core/guards/permission.guard';
import { Businesses } from './features/businesses/businesses';
import { BusinessReview } from './features/business-review/business-review';
import { SubscriptionPlans } from './features/subscription-plans/subscription-plans';
import { PlanSelection } from './features/plan-selection/plan-selection';
import { businessLocationGuard } from './core/guards/business-location.guard';
import { FranchiseLogin } from './features/authentication/franchise-login/franchise-login';
import { FranchiseDashboard } from './features/franchise-dashboard/franchise-dashboard';
import { PortalLogin } from './features/authentication/portal-login/portal-login';
import { adminPortalGuard, businessPortalGuard, franchisePortalGuard, portalEntryGuard } from './core/guards/portal.guard';

export const routes: Routes = [
  {
    path: '',
    component: AuthLayout,
    children: [
      {
        path: '',
        pathMatch: 'full',
        canMatch: [portalEntryGuard],
        component: PortalLogin,
      },
      {
        path: 'login',
        component: PortalLogin,
        canActivate: [guestGuard],
      },
      {
        path: 'admin-login',
        component: AdminLogin,
        canActivate: [adminPortalGuard, guestGuard],
      },
      { path: 'franchise-login', component: FranchiseLogin, canActivate: [franchisePortalGuard, guestGuard] },
      {
        path: 'onboard-business',
        component: Register,
        canActivate: [businessPortalGuard, guestGuard],
      },
      {
        path: 'register',
        redirectTo: 'onboard-business',
        pathMatch: 'full',
      },
      {
        path: 'forgot-password',
        component: ForgotPassword,
      },
      {
        path: 'reset-password',
        component: ResetPassword,
      },
      {
        path: 'change-password',
        component: ChangePassword,
        canActivate: [authGuard],
      },
    ],
  },
  { path: 'franchise/dashboard', component: FranchiseDashboard, canActivate: [franchisePortalGuard, authGuard] },
  {
    path: '',
    component: AdminLayout,
    canActivate: [authGuard],
    children: [
      {
        path: 'admin',
        canActivate: [adminPortalGuard],
        children: [
          {
            path: '',
            redirectTo: 'dashboard',
            pathMatch: 'full',
          },
          {
            path: 'dashboard',
            component: Dashboard,
            canActivate: [permissionGuard],
            data: { permission: 'PLATFORM_DASHBOARD_VIEW' },
          },
          {
            path: 'users',
            component: Users,
            canActivate: [permissionGuard],
            data: { permission: 'USER_VIEW' },
          },
          { path: 'roles', component: Roles, canActivate: [permissionGuard], data: { permission: 'ROLE_VIEW' } },
          { path: 'permissions', component: Permissions, canActivate: [permissionGuard], data: { permission: 'PERMISSION_VIEW' } },
          { path: 'businesses', component: Businesses, canActivate: [permissionGuard], data: { permission: 'PLATFORM_BUSINESS_VIEW' } },
          { path: 'businesses/:id', component: BusinessReview, canActivate: [permissionGuard], data: { permission: 'PLATFORM_BUSINESS_VIEW' } },
          { path: 'subscription-plans', component: SubscriptionPlans, canActivate: [permissionGuard], data: { permission: 'SUBSCRIPTION_PLAN_VIEW' } },
          { path: 'products', redirectTo: 'listing-approvals', pathMatch: 'full' },
          { path: 'products/create', redirectTo: 'listing-approvals', pathMatch: 'full' },
          { path: 'finance', component: Finance, canActivate: [permissionGuard], data: { permission: 'FINANCE_OVERVIEW_VIEW' } },
          { path: 'reports', component: Reports, canActivate: [permissionGuard], data: { permission: 'REPORT_VIEW' } },
          {
            path: 'listing-approvals',
            component: ListingApprovals,
            canActivate: [permissionGuard],
            data: { permission: 'LISTING_REVIEW_VIEW' },
          },
          {
            path: 'listing-approvals/:id',
            component: ListingApprovals,
            canActivate: [permissionGuard],
            data: { permission: 'LISTING_REVIEW_VIEW' },
          },
          {
            path: 'catalog',
            component: Catalog,
            canActivate: [permissionGuard],
            data: { permission: ['CATEGORY_VIEW', 'BRAND_VIEW', 'PROFIT_RULE_VIEW'] },
          },
          {
            path: 'locations',
            component: Stores,
            canActivate: [permissionGuard],
            data: { permission: 'LOCATION_VIEW' },
          },
          { path: 'orders', redirectTo: 'dashboard', pathMatch: 'full' },
          {
            path: 'app-content',
            component: Settings,
            canActivate: [permissionGuard],
            data: { permission: ['CONTENT_VIEW', 'POLICY_VIEW'] },
          },
          {
            path: 'support-tickets',
            component: SupportTickets,
            canActivate: [permissionGuard],
            data: { permission: 'SUPPORT_TICKET_VIEW' },
          },
          {
            path: 'faqs',
            component: Faqs,
            canActivate: [permissionGuard],
            data: { permission: 'FAQ_VIEW' },
          },
        ],
      },
      {
        path: 'business',
        canActivate: [businessPortalGuard],
        children: [
          {
            path: 'onboarding',
            component: BusinessOnboarding,
            canActivate: [businessOwnerOnlyGuard],
          },
          { path: 'plan-selection', component: PlanSelection, canActivate: [businessPlanSelectionGuard] },
          {
            path: '',
            canActivate: [businessOnboardingGuard],
            children: [
              { path: '', redirectTo: 'dashboard', pathMatch: 'full' },
              { path: 'dashboard', component: Dashboard, canActivate: [permissionGuard], data: { permission: 'DASHBOARD_VIEW' } },
              { path: 'products', component: Products, canActivate: [permissionGuard, businessLocationGuard], data: { listOnly: true, permission: 'PRODUCT_VIEW' } },
              { path: 'products/create', component: Products, canActivate: [permissionGuard, businessLocationGuard], data: { createOnly: true, permission: 'PRODUCT_CREATE' } },
              {
                path: 'purchases',
                component: Purchases,
                canActivate: [permissionGuard, businessLocationGuard],
                data: {
                  permission: [
                    'PROCUREMENT_COMPANY_USE',
                    'PROCUREMENT_NEW_PRODUCT',
                    'PROCUREMENT_USED_PRODUCT',
                  ],
                },
              },
              {
                path: 'purchase-list',
                component: PurchaseList,
                canActivate: [permissionGuard, businessLocationGuard],
                data: { permission: 'PURCHASE_VIEW' },
              },
              {
                path: 'sales',
                component: Sales,
                canActivate: [permissionGuard, businessLocationGuard],
                data: { permission: ['SALE_VIEW', 'SALE_CREATE'] },
              },
              { path: 'locations', component: Stores, canActivate: [permissionGuard], data: { permission: 'LOCATION_VIEW' } },
              { path: 'stores', redirectTo: 'locations', pathMatch: 'full' },
              {
                path: 'inventory',
                component: Inventory,
                canActivate: [permissionGuard, businessLocationGuard],
                data: { permission: 'INVENTORY_VIEW' },
              },
              { path: 'finance', component: Finance, canActivate: [permissionGuard, businessLocationGuard], data: { permission: 'FINANCE_OVERVIEW_VIEW' } },
              { path: 'profit-rules', component: Catalog, canActivate: [permissionGuard, businessLocationGuard], data: { permission: 'PROFIT_RULE_VIEW', profitOnly: true } },
              { path: 'orders', component: Billing, canActivate: [permissionGuard, businessLocationGuard], data: { permission: 'ORDER_VIEW' } },
              { path: 'internal-sales', component: Billing, canActivate: [permissionGuard, businessLocationGuard], data: { permission: 'SALE_VIEW' } },
              { path: 'reports', component: Reports, canActivate: [permissionGuard, businessLocationGuard], data: { permission: 'REPORT_VIEW' } },
              {
                path: 'users',
                component: Users,
                canActivate: [permissionGuard],
                data: { permission: 'USER_VIEW' },
              },
              {
                path: 'roles',
                component: Roles,
                canActivate: [permissionGuard],
                data: { permission: 'ROLE_VIEW' },
              },
              {
                path: 'permissions',
                component: Permissions,
                canActivate: [permissionGuard],
                data: { permission: 'PERMISSION_VIEW' },
              },
            ],
          },
        ],
      },
    ],
  },
  {
    path: '**',
    canMatch: [portalEntryGuard],
    component: PortalLogin,
  },
];
